// src/store/collection/collectionStore.ts
import { create } from "zustand";
import type {
  CollectionNode,
  CollectionHierarchyQuery,
  PageContent,
  CollectionUpsertPayload,
} from "src/types/collection/collectionTypes";
import {
  getCollectionHierarchyApi,
  createCollectionApi,
  updateCollectionApi,
  deleteCollectionApi,
} from "src/api/collection/collectionApi";
import { normalizeCollectionPage } from "./collection.mappers";

type CollectionState = {
  // query
  query: CollectionHierarchyQuery;

  // server data
  pageData: PageContent<CollectionNode> | null;
  loading: boolean;
  error: string | null;

  // UI expand state (tree open/close)
  expandedIds: Record<number, boolean>; // collectionId -> expanded?

  // actions
  setQuery: (patch: Partial<CollectionHierarchyQuery>) => void;
  fetchHierarchy: () => Promise<void>;
  toggleExpand: (collectionId: number) => void;
  expandAllVisible: () => void;
  collapseAll: () => void;

  createCollection: (args: {
    payload: CollectionUpsertPayload;
    thumbnailFile?: File | null;
  }) => Promise<void>;

  updateCollection: (args: {
    id: number;
    payload: CollectionUpsertPayload;
    thumbnailFile?: File | null;
  }) => Promise<void>;

  deleteCollection: (id: number) => Promise<void>;
  toggleCollectionActive: (node: CollectionNode) => Promise<void>;
};

const defaultQuery: CollectionHierarchyQuery = {
  collectionName: "",
  parentCollectionId: undefined,
  depth: undefined,
  isActive: undefined,
  isFeatured: undefined,
  page: 0,
  size: 20,
  order: "displayOrder,asc",
};

export const useCollectionStore = create<CollectionState>((set, get) => ({
  query: defaultQuery,

  pageData: null,
  loading: false,
  error: null,

  expandedIds: {},

  setQuery: (patch) => set((s) => ({ query: { ...s.query, ...patch } })),

  fetchHierarchy: async () => {
    const { query } = get();
    set({ loading: true, error: null });

    try {
      const res = await getCollectionHierarchyApi(query);
      if (res.statusCode !== 1) throw new Error(res.message || "API error");

      const normalized = normalizeCollectionPage(res.data); // ✅ 여기!
      set({ pageData: normalized, loading: false });
    } catch (e: any) {
      set({
        loading: false,
        error: e?.message || "컬렉션 조회 중 오류가 발생했어요.",
      });
    }
  },

  toggleExpand: (collectionId) =>
    set((s) => ({
      expandedIds: {
        ...s.expandedIds,
        [collectionId]: !s.expandedIds[collectionId],
      },
    })),

  expandAllVisible: () => {
    const pageData = get().pageData;
    if (!pageData) return;

    const next: Record<number, boolean> = {};
    const walk = (nodes: CollectionNode[]) => {
      nodes.forEach((n) => {
        if (n.children?.length) next[n.collectionId] = true;
        if (n.children?.length) walk(n.children);
      });
    };
    walk(pageData.content);
    set({ expandedIds: next });
  },

  collapseAll: () => set({ expandedIds: {} }),

  createCollection: async ({ payload }) => {
    set({ loading: true, error: null });
    try {
      // if (!payload.thumbnailFileGrpId) {
      //   throw new Error("썸네일 이미지를 선택해주세요.");
      // }

      const res = await createCollectionApi(payload);
      if (res.statusCode !== 1) throw new Error(res.message || "API error");
      set({ loading: false });
      await get().fetchHierarchy();
    } catch (e: any) {
      set({ loading: false, error: e?.message || "생성 중 오류가 발생했어요." });
    }
  },

  updateCollection: async ({ id, payload }) => {
    set({ loading: true, error: null });
    try {
      const res = await updateCollectionApi(id, payload);
      if (res.statusCode !== 1) throw new Error(res.message || "API error");
      set({ loading: false });
      await get().fetchHierarchy();
    } catch (e: any) {
      set({ loading: false, error: e?.message || "수정 중 오류가 발생했어요." });
    }
  },

  deleteCollection: async (id) => {
    set({ loading: true, error: null });
    try {
      const res = await deleteCollectionApi(id);
      if (res.statusCode !== 1) throw new Error(res.message || "API error");
      set({ loading: false });
      await get().fetchHierarchy();
    } catch (e: any) {
      set({ loading: false, error: e?.message || "삭제 중 오류가 발생했어요." });
    }
  },

  toggleCollectionActive: async (node) => {
    const payload: CollectionUpsertPayload = {
      parentCollectionId: node.parentCollectionId,
      collectionName: node.collectionName,
      collectionCode: node.collectionCode,
      collectionDescription: node.collectionDescription,
      collectionContent: node.collectionContent,
      displayOrder: node.displayOrder,
      displayStartDate: node.displayStartDate ?? "",
      displayEndDate: node.displayEndDate ?? "",
      isFeatured: node.isFeatured,
      isActive: !node.isActive,
      thumbnailFileGrpId: node.thumbnailFileGrpId,
    };
    set({ loading: true, error: null });
    try {
      const res = await updateCollectionApi(node.collectionId, payload);
      if (res.statusCode !== 1) throw new Error(res.message || "API error");
      set({ loading: false });
      await get().fetchHierarchy();
    } catch (e: any) {
      set({ loading: false, error: e?.message || "노출 상태 변경 중 오류가 발생했어요." });
    }
  },
}));
